import { useRef, useState, type ChangeEvent } from "react";

import { SearchIcon, XIcon } from "../lib/icons";
import { Input, type InputProps } from "./input";
import { Kbd } from "./kbd";

export interface SearchInputProps extends Omit<InputProps, "type" | "leading" | "trailing"> {
  /** Appelé au clic sur la croix, en plus du `onChange` habituel. */
  onClear?: () => void;
  /** Raccourci clavier affiché à droite tant que le champ est vide (ex. `["⌘", "K"]`). */
  shortcut?: string[];
}

/**
 * Champ de recherche.
 *
 * `type="search"` et non `type="text"` : le clavier mobile affiche alors une
 * touche « Rechercher » plutôt que « Entrée », et les navigateurs proposent
 * l'historique de recherche.
 *
 * La croix d'effacement n'apparaît que lorsqu'il y a quelque chose à effacer —
 * un bouton d'action grisé en permanence est du bruit.
 */
export function SearchInput({ onClear, shortcut, value, onChange, ...props }: SearchInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [internal, setInternal] = useState("");

  const isControlled = value !== undefined;
  const current = isControlled ? String(value) : internal;
  const hasValue = current.length > 0;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!isControlled) setInternal(event.target.value);
    onChange?.(event);
  };

  const clear = () => {
    if (!isControlled) setInternal("");
    onClear?.();
    // On rend le focus au champ : l'utilisateur veut retaper, pas cliquer à nouveau.
    inputRef.current?.focus();
  };

  return (
    <Input
      ref={inputRef}
      type="search"
      value={isControlled ? value : internal}
      onChange={handleChange}
      leading={<SearchIcon />}
      trailing={
        hasValue ? (
          <button
            type="button"
            onClick={clear}
            aria-label="Effacer la recherche"
            className="rounded-full p-0.5 text-fg-muted transition-colors duration-fast hover:bg-surface-sunken hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            <XIcon className="size-3.5" />
          </button>
        ) : shortcut ? (
          <span className="flex gap-1" aria-hidden="true">
            {shortcut.map((key) => (
              <Kbd key={key}>{key}</Kbd>
            ))}
          </span>
        ) : undefined
      }
      // Croix native de `type="search"` : elle double la nôtre et n'est stylable
      // sur aucun navigateur.
      className="[&::-webkit-search-cancel-button]:hidden"
      {...props}
    />
  );
}
